import { IconButton, Stack, Tooltip, Typography } from "@mui/material"
import PropTypes from "prop-types"
import { useState, useRef, useMemo } from "react"

import { rootStore } from "stores/root-store"

import AlertDialog from "components/common/alert-dialog"
import { FormWrapper } from "components/common-styles"
import FreightEnquiryInfo from "./freight-enquiry-info"
import CommonFormActions from "components/common/common-form-actions"
import { ArrowBack } from "@mui/icons-material"
import { trackEvent } from "utils/analytics"

const escapeHtml = value => {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;")
}

const buildHtmlLine = (label, value) => {
  return `<div><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</div>`
}

const toBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.readAsDataURL(file)

    reader.onload = () => {
      // remove "data:...;base64," prefix
      const base64 = reader.result.split(",")[1]
      resolve(base64)
    }

    reader.onerror = error => reject(error instanceof Error ? error : new Error("Failed to read attachment file"))
  })

const FreightEnquiryForm = ({ onClose, onSave }) => {
  const initialValues = useMemo(() => {
    return {
      first_name: "",
      last_name: "",
      email: "",
      enquiry_about: "",
      enquiry_description: "",
      your_reference: "",
      tracking_id: "",
      order_lodgement_date: null,
      service_used: "",
      item_contents_description: "",
      attachment: null,
      attachment_name: "",
      addressee_first_name: "",
      addressee_last_name: "",
      street_address: "",
      address_line_2: "",
      city: "",
      state: "",
      postal_code: "",
      country: "Australia",
    }
  }, [])

  const [openAlert, setOpenAlert] = useState(false)
  const [alertContent, setAlertContent] = useState("")

  const formRef = useRef()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [detailInfo] = useState(initialValues)
  const [wasSuccess, setWasSuccess] = useState(true)

  const handleSubmit = async () => {
    const resInfo = await formRef.current.getFormDataIfValid()
    if (!resInfo) return

    let attachments = []

    if (resInfo.attachment) {
      const base64 = await toBase64(resInfo.attachment)
      attachments.push({
        Name: resInfo.attachment_name,
        ContentBytes: base64,
      })
    }

    setIsSubmitting(true)
    setOpenAlert(true)

    const body = [
      buildHtmlLine("First Name", resInfo.first_name),
      buildHtmlLine("Last Name", resInfo.last_name),
      buildHtmlLine("Email", resInfo.email),
      buildHtmlLine("What is your enquiry about", resInfo.enquiry_about),
      buildHtmlLine("Please provide a detailed description of your enquiry", resInfo.enquiry_description),
      buildHtmlLine("Your Reference (Order Number/Tracking Number)", resInfo.your_reference),
      buildHtmlLine("Order Lodgement Date", resInfo.order_lodgement_date),
      buildHtmlLine("Service Used", resInfo.service_used),
      buildHtmlLine("Item Contents Description", resInfo.item_contents_description),
      buildHtmlLine("Addressee First Name", resInfo.addressee_first_name),
      buildHtmlLine("Addressee Last Name", resInfo.addressee_last_name),
      buildHtmlLine("Street Address", resInfo.street_address),
      buildHtmlLine("Address Line 2", resInfo.address_line_2),
      buildHtmlLine("City", resInfo.city),
      buildHtmlLine("State", resInfo.state),
      buildHtmlLine("Postal Code", resInfo.postal_code),
      buildHtmlLine("Country", resInfo.country),
    ].join("")

    const payload = {
      to: "freightenquiries@estorelogistics.com.au",
      subject: `Freight Enquiry - ${rootStore.company}`,
      body,
      attachments,
    }
    const res = await rootStore.sendEmail(payload)
    trackEvent("freight_enquiry_submit", { result: res ? "success" : "failed" })
    // Customers share logins, so nothing from this enquiry may be left behind for the next person
    if (res) formRef.current.reset()

    setIsSubmitting(false)
    setWasSuccess(res)
    setAlertContent(res ? "Freight Enquiry Submitted Successfully" : "Failed to Submit Freight Enquiry")
  }

  const onOK = () => {
    setOpenAlert(false)
    if (wasSuccess && onSave) {
      onSave()
    }
  }

  const handleClose = () => {
    if (onClose) onClose()
  }

  return (
    <>
      <FormWrapper spacing={2}>
        <Stack direction={"row"} spacing={2} alignItems="center">
          {onClose && (
            <Tooltip placement="top" title="Back">
              <IconButton onClick={handleClose}>
                <ArrowBack />
              </IconButton>
            </Tooltip>
          )}

          <Typography variant="h5">Provide us with your freight enquiry details for lodgement to the shipping carrier</Typography>
        </Stack>

        <FreightEnquiryInfo detailInfo={detailInfo} ref={formRef} />

        <CommonFormActions isSubmitting={isSubmitting} handleSubmit={handleSubmit} handleClose={handleClose} />
      </FormWrapper>
      <AlertDialog isProgress={isSubmitting} content={alertContent} open={openAlert} onClose={() => setOpenAlert(false)} onOK={onOK} />
    </>
  )
}

FreightEnquiryForm.propTypes = {
  onClose: PropTypes.func,
  onSave: PropTypes.func,
}

export default FreightEnquiryForm
